import { useEffect, useState } from "react";
import { Button } from "../../ui/Button.js";
import { Input, Label, Select } from "../../ui/Input.js";
import { ModalOverlay } from "../../ui/Modal.js";
import { RecipientInput } from "./RecipientInput.js";
import { RichTextEditor } from "./RichTextEditor.js";
import { inboxRequest, signatureBlock, type EmailTemplate } from "./inboxApi.js";

export interface ComposeDraft {
  to?: string[];
  cc?: string[];
  subject?: string;
  html?: string;
}

/**
 * Write and send an email from the contracting address. Starts blank, from a
 * template, or as a reply. The sender's saved signature is added below the
 * message unless they untick it.
 */
export function ComposeEmailModal({
  initial,
  onClose,
  onSent,
}: {
  initial?: ComposeDraft;
  onClose: () => void;
  onSent: () => void;
}) {
  const [to, setTo] = useState<string[]>(initial?.to ?? []);
  const [cc, setCc] = useState<string[]>(initial?.cc ?? []);
  const [bcc, setBcc] = useState<string[]>([]);
  const [showCopies, setShowCopies] = useState(Boolean(initial?.cc?.length));
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [html, setHtml] = useState(initial?.html ?? "");
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [signature, setSignature] = useState("");
  const [useSignature, setUseSignature] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void inboxRequest<EmailTemplate[]>("/templates").then(setTemplates).catch(() => {});
    void inboxRequest<{ html: string }>("/signature").then((data) => setSignature(data.html)).catch(() => {});
  }, []);

  function applyTemplate(id: string) {
    const template = templates.find((entry) => entry.id === id);
    if (!template) return;
    if (template.subject) setSubject(template.subject);
    // A reply keeps the quoted message under the template.
    setHtml(template.html + (initial?.html ? initial.html : ""));
  }

  async function send() {
    setSending(true);
    setError(null);
    try {
      await inboxRequest("/send", {
        method: "POST",
        body: JSON.stringify({
          to,
          cc,
          bcc,
          subject,
          html: html + (useSignature ? signatureBlock(signature) : ""),
        }),
      });
      onSent();
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "The email was not sent.");
    } finally {
      setSending(false);
    }
  }

  return (
    <ModalOverlay
      onClose={sending ? undefined : onClose}
      ariaLabel="New email"
      className="[&>div]:max-w-3xl [&>div]:max-h-[92vh] [&>div]:overflow-y-auto"
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-content-primary">New email</h2>
            <p className="text-xs text-content-muted">Sends from the contracting address. Replies show up under Inbound.</p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={sending}>
            Close
          </Button>
        </div>

        <RecipientInput label="To" value={to} onChange={setTo} autoFocus={!initial?.to?.length} />
        {showCopies ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <RecipientInput label="Cc" value={cc} onChange={setCc} />
            <RecipientInput label="Bcc" value={bcc} onChange={setBcc} />
          </div>
        ) : (
          <button type="button" className="text-xs font-semibold text-accent hover:underline" onClick={() => setShowCopies(true)}>
            Add Cc or Bcc
          </button>
        )}

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_14rem]">
          <Label>
            Subject
            <Input className="mt-1.5" value={subject} onChange={(event) => setSubject(event.target.value)} maxLength={300} />
          </Label>
          <Label>
            Template
            <Select
              className="mt-1.5"
              value=""
              onChange={(event) => applyTemplate(event.target.value)}
              disabled={templates.length === 0}
            >
              <option value="">{templates.length ? "Start from a template…" : "No templates yet"}</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </Select>
          </Label>
        </div>

        <RichTextEditor label="Message" value={html} onChange={setHtml} placeholder="Write your message" minHeight="14rem" />

        <label className="flex items-center gap-2 text-sm text-content-secondary">
          <input type="checkbox" checked={useSignature} onChange={(event) => setUseSignature(event.target.checked)} disabled={!signature} />
          {signature ? "Add my signature" : "No signature saved yet (set one under Templates & signature)"}
        </label>
        {useSignature && signature && (
          <div
            className="rich-text-body rounded-lg border border-dashed border-border-default px-3 py-2 text-sm text-content-secondary"
            // The signature was cleaned server-side when it was saved.
            dangerouslySetInnerHTML={{ __html: signatureBlock(signature) }}
          />
        )}

        {error && (
          <p role="alert" className="rounded-lg border border-status-rejected-border bg-status-rejected-bg px-4 py-3 text-sm text-status-rejected-text">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={sending}>
            Discard
          </Button>
          <Button type="submit" disabled={sending || to.length === 0}>
            {sending ? "Sending…" : "Send"}
          </Button>
        </div>
      </form>
    </ModalOverlay>
  );
}
